// The Test users tab's pure logic: who the search box names, expiry choices
// and how an expiry reads.

import type { EngineName, Profile } from "../../services/voiceEngines";

// Expiry choices in days; null never expires.
export const EXPIRY_DAYS: (number | null)[] = [1, 7, 30, null];

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

// An email when the text has an "@", else a uid; null when blank.
export function parseWho(text: string): { email: string } | { uid: string } | null {
  const t = text.trim();
  if (!t) return null;
  return t.includes("@") ? { email: t } : { uid: t };
}

// The ISO instant `days` from `now`; null for never.
export function expiresAtFor(days: number | null, now: Date): string | null {
  return days === null ? null : new Date(now.getTime() + days * DAY_MS).toISOString();
}

// The expiry choice closest to an existing override's, for its edit form.
export function expiryChoice(expiresAt: string | null, now: Date): number | null {
  if (!expiresAt) return null;
  const left = (new Date(expiresAt).getTime() - now.getTime()) / DAY_MS;
  const choices = EXPIRY_DAYS.filter((d): d is number => d !== null);
  return choices.reduce((best, d) => (Math.abs(d - left) < Math.abs(best - left) ? d : best), choices[0]);
}

export type ExpiryTone = "expired" | "soon" | "later" | "never";

// "in 12 days", "in 5 hours", "expired 2 days ago", "never".
export function expiryText(expiresAt: string | null, now: Date): { text: string; tone: ExpiryTone } {
  if (!expiresAt) return { text: "never", tone: "never" };
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (Number.isNaN(ms)) return { text: "unknown", tone: "later" };
  const span = (abs: number) => {
    if (abs >= DAY_MS) {
      const d = Math.floor(abs / DAY_MS);
      return `${d} day${d === 1 ? "" : "s"}`;
    }
    const h = Math.max(1, Math.floor(abs / HOUR_MS));
    return `${h} hour${h === 1 ? "" : "s"}`;
  };
  if (ms <= 0) return { text: `expired ${span(-ms)} ago`, tone: "expired" };
  return { text: `in ${span(ms)}`, tone: ms <= 5 * DAY_MS ? "soon" : "later" };
}

// The profiles an override (or arm) of `engine` may name: active stored
// profiles of that engine, the built-in first.
export function profileChoices(engine: EngineName, profiles: Profile[]): Profile[] {
  return profiles.filter((p) => p.engine === engine && (p.builtin || p.status === "active"));
}
